import { PageScroll } from "@Components/Page";
import { useDebounce } from "@Hooks/useDebounce";
import { styled } from "@mui/material/styles";
import { Button, EmptyState, EyeIcon, GroupedList, IconButton, ListRow, PageTitle, PencilIcon, PlusIcon, SearchField, SearchIcon, SectionLabel, SegmentedControl, StarIcon, TrashIcon } from "@UI";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
const matchesQuery = (query)=>(prompt)=>prompt.title.toLowerCase().includes(query.toLowerCase()) || prompt.prompt.toLowerCase().includes(query.toLowerCase());
const SHOWN_ACTIONS = {
    position: "static",
    width: "auto",
    height: "auto",
    overflow: "visible",
    clipPath: "none"
};
const PromptList = styled(GroupedList)({
    "& > .jc-row": {
        "& .row-actions": {
            display: "flex"
        },
        "& .row-actions:not(:focus-within)": {
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clipPath: "inset(50%)",
            whiteSpace: "nowrap"
        },
        "&:hover .row-actions, &:focus-within .row-actions": SHOWN_ACTIONS,
        "&:hover .row-chevron, &:focus-within .row-chevron": {
            display: "none"
        },
        "@media (hover: none)": {
            "& .row-actions:not(:focus-within)": SHOWN_ACTIONS,
            "& .row-chevron": {
                display: "none"
            }
        },
        "& .row-trailing, & .row-actions": {
            gap: "var(--j-sp-0-5)",
            alignItems: "center"
        },
        "& .row-trailing button:hover": {
            background: "var(--j-well)"
        },
        "& .is-favourite, & .is-favourite:hover": {
            color: "var(--j-ink-2)",
            background: "none"
        }
    }
});
const EMPTY_KEYS = {
    private: {
        none: "prompts.empty.private",
        noMatch: "prompts.noMatch.private"
    },
    organization: {
        none: "prompts.empty.team",
        noMatch: "prompts.noMatch.team"
    },
    juristic: {
        none: "prompts.empty.juristic",
        noMatch: "prompts.noMatch.juristic"
    }
};
export function PromptOverview({ type, onChangePromptType, privatePrompts, teamPrompts, juristicPrompts, onNewPrompt, onEditPrompt, onUsePrompt, onFavouritePrompt, onDeletePrompt }) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
    const rootRef = useRef(null);
    const pendingStarFocus = useRef(null);
    useDebounce(()=>setDebouncedQuery(searchQuery), [
        searchQuery
    ], 300);
    const isPrivate = type === "private";
    const isEditable = type !== "juristic";
    const { prompts, favourites } = useMemo(()=>{
        const source = {
            private: privatePrompts,
            organization: teamPrompts,
            juristic: juristicPrompts
        }[type];
        const all = isPrivate ? source.filter((p)=>!p.isFavourite) : source;
        const favs = isPrivate ? source.filter((p)=>p.isFavourite) : [];
        if (!debouncedQuery) return {
            prompts: all,
            favourites: favs
        };
        const filter = matchesQuery(debouncedQuery);
        return {
            prompts: all.filter(filter),
            favourites: favs.filter(filter)
        };
    }, [
        type,
        isPrivate,
        privatePrompts,
        teamPrompts,
        juristicPrompts,
        debouncedQuery
    ]);
    useLayoutEffect(()=>{
        const id = pendingStarFocus.current;
        if (id === null) return;
        pendingStarFocus.current = null;
        const active = document.activeElement;
        if (active && active !== document.body) return;
        rootRef.current?.querySelector(`[data-star="${id}"]`)?.focus();
    }, [
        prompts,
        favourites
    ]);
    const tabs = [
        {
            value: "private",
            label: t("private")
        },
        {
            value: "organization",
            label: t("prompts.team")
        },
        ...juristicPrompts.length ? [
            {
                value: "juristic",
                label: t("prompts.juristic")
            }
        ] : []
    ];
    const renderRow = (prompt)=>{
        const descriptionId = `prompt-desc-${prompt.id}`;
        const star = isPrivate && /*#__PURE__*/ React.createElement(IconButton, {
            size: "sm",
            "data-star": prompt.id,
            className: prompt.isFavourite ? "is-favourite" : undefined,
            "aria-label": t(prompt.isFavourite ? "prompts.unfavourite" : "prompts.favouriteAction"),
            "aria-pressed": prompt.isFavourite,
            onClick: ()=>{
                pendingStarFocus.current = prompt.id;
                onFavouritePrompt(prompt);
            }
        }, /*#__PURE__*/ React.createElement(StarIcon, {
            style: prompt.isFavourite ? {
                fill: "currentColor"
            } : undefined
        }));
        return /*#__PURE__*/ React.createElement(ListRow, {
            key: prompt.id,
            as: "div",
            "data-interactive": true,
            title: /*#__PURE__*/ React.createElement("button", {
                type: "button",
                className: "row-stretch",
                "aria-describedby": descriptionId,
                onClick: ()=>onUsePrompt(prompt.prompt)
            }, prompt.title),
            description: /*#__PURE__*/ React.createElement("span", {
                id: descriptionId
            }, prompt.prompt),
            chevron: !prompt.isFavourite,
            trailing: /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
                className: "row-actions"
            }, /*#__PURE__*/ React.createElement(IconButton, {
                size: "sm",
                "aria-label": t(isEditable ? "prompts.edit" : "prompts.view"),
                onClick: ()=>onEditPrompt(prompt)
            }, isEditable ? /*#__PURE__*/ React.createElement(PencilIcon, null) : /*#__PURE__*/ React.createElement(EyeIcon, null)), !prompt.isFavourite && star, isEditable && /*#__PURE__*/ React.createElement(IconButton, {
                size: "sm",
                "aria-label": t("prompts.delete"),
                onClick: ()=>onDeletePrompt(prompt)
            }, /*#__PURE__*/ React.createElement(TrashIcon, null))), prompt.isFavourite && star)
        });
    };
    const isEmpty = !prompts.length && !favourites.length;
    const emptyKeys = EMPTY_KEYS[type];
    return /*#__PURE__*/ React.createElement(PageScroll, {
        ref: rootRef
    }, /*#__PURE__*/ React.createElement(PageTitle, {
        title: t("prompts.library.single"),
        lede: t("prompts.library.description"),
        action: isEditable && /*#__PURE__*/ React.createElement(Button, {
            variant: "tonal",
            size: "sm",
            startIcon: /*#__PURE__*/ React.createElement(PlusIcon, {
                size: 14
            }),
            onClick: onNewPrompt
        }, t("prompts.new"))
    }), /*#__PURE__*/ React.createElement(SegmentedControl, {
        role: "radiogroup",
        "aria-label": t("prompts.library.single"),
        options: tabs,
        value: type,
        onChange: (next)=>{
            onChangePromptType(next);
        }
    }), /*#__PURE__*/ React.createElement(SearchField, {
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: t("prompts.search"),
        loading: searchQuery !== debouncedQuery
    }), favourites.length > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SectionLabel, null, t("prompts.favourite")), /*#__PURE__*/ React.createElement(PromptList, null, favourites.map(renderRow))), prompts.length > 0 && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SectionLabel, null, t("prompts.allCount", {
        count: prompts.length
    })), /*#__PURE__*/ React.createElement(PromptList, null, prompts.map(renderRow))), isEmpty && debouncedQuery && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(SearchIcon, null),
        title: t(emptyKeys.noMatch),
        text: t(isEditable ? "prompts.noMatch.text" : "prompts.noMatch.textReadOnly"),
        actions: /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Button, {
            variant: "outline",
            size: "lg",
            onClick: ()=>setSearchQuery("")
        }, t("ui.clearSearch")), isEditable && /*#__PURE__*/ React.createElement(Button, {
            size: "lg",
            onClick: onNewPrompt
        }, t("prompts.newPrompt")))
    }), isEmpty && !debouncedQuery && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(StarIcon, null),
        title: t(emptyKeys.none),
        text: isEditable ? t("prompts.empty.text") : undefined,
        actions: isEditable && /*#__PURE__*/ React.createElement(Button, {
            size: "lg",
            onClick: onNewPrompt
        }, t("prompts.newPrompt"))
    }));
}
